import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./router";
import { isStaticPage, StaticTree } from "./staticPages";
import "./styles.css";

const queryClient = new QueryClient();

// iOS standalone reports a stale viewport on first paint, so CSS units
// (100%, 100dvh) can leave a band under the tab bar. Measure the real
// window height and re-measure as iOS settles and when the keyboard moves it.
// When the keyboard opens, iOS shrinks the visual viewport and may also
// offset it; the fixed body follows both so the composer sits on the
// keyboard and the tab bar stays put instead of the page jumping.
function setAppHeight() {
  const vv = window.visualViewport;
  // The root may be zoomed on big desktop windows (styles.css); lengths inside it are in zoomed px.
  const zoom = parseFloat(getComputedStyle(document.documentElement).zoom as string) || 1;
  const h = (vv?.height ?? window.innerHeight) / zoom;
  const top = (vv?.offsetTop ?? 0) / zoom;
  const root = document.documentElement.style;
  root.setProperty("--app-h", `${Math.round(h)}px`);
  root.setProperty("--app-top", `${Math.round(top)}px`);
  // iOS also scrolls the window to "reveal" the field; undo it.
  if (window.scrollY !== 0) window.scrollTo(0, 0);
}
setAppHeight();
for (const t of [50, 250, 1000]) setTimeout(setAppHeight, t);
window.addEventListener("resize", setAppHeight);
window.addEventListener("orientationchange", setAppHeight);
window.visualViewport?.addEventListener("resize", setAppHeight);
window.visualViewport?.addEventListener("scroll", setAppHeight);

// "/" and "/signin" arrive as prebuilt HTML (prerender.tsx): hydrate that page on its
// own, without the router. Everything else is the app.
const root = document.getElementById("root")!;
const page = root.dataset.static;
if (isStaticPage(page)) {
  hydrateRoot(root, <StaticTree page={page} client={queryClient} />);
} else {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}
